import {
  BillingPortalInput,
  BillingPortalResult,
  CreditBalance,
  CreditPack,
  CreditService,
  CreditTransaction,
  GetCreditTransactionsInput,
  PurchaseCreditsInput,
  PurchaseCreditsResult,
} from '@services';
import { makeAutoObservable, runInAction } from 'mobx';

class CreditStore {
  balance: CreditBalance | null = null;
  creditPacks: CreditPack[] = [];
  transactions: CreditTransaction[] = [];
  transactionTotal: number = 0;
  loading: boolean = false;
  submitLoading: boolean = false;

  /**
   * Shared by concurrent getBalance callers. Several components ask for the balance as
   * they mount — the demo banner and the vehicle search sit in the same drawer — and
   * without this each one would fire its own request for the same number.
   */
  balanceRequest: Promise<void> | null = null;

  constructor() {
    // balanceRequest is internal plumbing, not view state, and it is assigned after an
    // await — leaving it observable would mean mutating an observable outside an action.
    makeAutoObservable(this, { balanceRequest: false });
  }

  async getBalance() {
    if (this.balanceRequest) return this.balanceRequest;

    this.setLoading(true);

    this.balanceRequest = (async () => {
      try {
        const result = await CreditService.getBalance();
        runInAction(() => {
          this.balance = result;
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.setLoading(false);
        this.balanceRequest = null;
      }
    })();

    return this.balanceRequest;
  }

  /**
   * Spends one vehicle lookup credit and stores the balance the server reports back, so
   * the remaining count on screen always comes from the server rather than being counted
   * down locally.
   */
  async consumeVehicleLookup() {
    try {
      const result = await CreditService.consumeVehicleLookup();
      runInAction(() => {
        this.balance = result;
      });
    } catch (error) {
      console.error(error);
    }
  }

  async getCreditPacks() {
    this.setLoading(true);
    try {
      const result = await CreditService.getCreditPacks();
      runInAction(() => {
        this.creditPacks = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getTransactions(input: GetCreditTransactionsInput) {
    this.setLoading(true);
    try {
      const result = await CreditService.getTransactions(input);
      runInAction(() => {
        this.transactions = result.items;
        this.transactionTotal = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async purchaseCredits(input: PurchaseCreditsInput): Promise<PurchaseCreditsResult | undefined> {
    this.setSubmitLoading(true);
    try {
      const result = await CreditService.purchaseCredits(input);
      this.setSubmitLoading(false);
      return result;
    } catch (error) {
      this.setSubmitLoading(false);
      console.error(error);
      throw error;
    }
  }

  async getBillingPortalUrl(input: BillingPortalInput): Promise<BillingPortalResult | undefined> {
    this.setSubmitLoading(true);
    try {
      const result = await CreditService.getBillingPortalUrl(input);
      this.setSubmitLoading(false);
      return result;
    } catch (error) {
      this.setSubmitLoading(false);
      console.error(error);
      throw error;
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSubmitLoading = (value: boolean) => {
    runInAction(() => {
      this.submitLoading = value;
    });
  };

  clear() {
    runInAction(() => {
      this.balance = null;
      this.creditPacks = [];
      this.transactions = [];
      this.transactionTotal = 0;
    });
  }
}

export default CreditStore;
